<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Configurações de Entrega</h1>
      <button
        @click="showAddModal = true"
        class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
      >
        + Novo Bairro
      </button>
    </div>
    
    <!-- Taxa única de entrega -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">Taxa única de entrega</h2>
          <p class="text-sm text-gray-500 mt-1">
            Defina um valor único de entrega para todos os bairros. Ao ativar, as taxas por bairro cadastradas serão desativadas.
          </p>
        </div>
        <label class="flex items-center cursor-pointer shrink-0">
          <input v-model="flatFeeEnabled" type="checkbox" class="w-4 h-4 text-orange-600 rounded">
          <span class="ml-2 text-sm font-medium text-gray-700">Ativar taxa única</span>
        </label>
      </div>
      
      <div class="mt-4 flex flex-wrap items-end gap-3">
        <div v-if="flatFeeEnabled" class="w-48">
          <label class="block text-sm font-medium text-gray-700 mb-1">Valor da taxa (R$)</label>
          <input
            v-model.number="flatFeeValueReais"
            type="number"
            min="0"
            step="0.01"
            class="w-full border rounded-lg px-3 py-2"
            placeholder="0.00"
          >
        </div>
        <button
          @click="saveFlatFee"
          :disabled="savingFlatFee"
          class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50"
        >
          {{ savingFlatFee ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
      
      <div v-if="flatFeeActive" class="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800">
        Taxa única ativa: <strong>{{ formatCurrency(currentFlatFee || 0) }}</strong> para todos os bairros. As taxas por bairro abaixo estão desativadas.
      </div>
    </div>
    
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bairro</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cidade</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taxa de Entrega</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tempo Estimado</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="neighborhood in neighborhoods" :key="neighborhood.id" class="hover:bg-gray-50">
            <td class="px-6 py-4 font-medium text-gray-900">{{ neighborhood.name }}</td>
            <td class="px-6 py-4 text-gray-500">{{ neighborhood.city }}</td>
            <td class="px-6 py-4 font-medium">{{ formatCurrency(neighborhood.delivery_fee) }}</td>
            <td class="px-6 py-4">{{ neighborhood.estimated_time }} min</td>
            <td class="px-6 py-4">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" 
                :class="neighborhood.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                {{ neighborhood.is_active ? 'Ativo' : 'Inativo' }}
              </span>
            </td>
            <td class="px-6 py-4">
              <button @click="editNeighborhood(neighborhood)" class="text-blue-600 hover:text-blue-800 font-medium mr-3">
                Editar
              </button>
              <button @click="toggleActive(neighborhood)" class="text-gray-600 hover:text-gray-800 mr-3">
                {{ neighborhood.is_active ? 'Desativar' : 'Ativar' }}
              </button>
              <button @click="deleteNeighborhood(neighborhood)" class="text-red-600 hover:text-red-800 font-medium">
                Excluir
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <div v-if="neighborhoods.length === 0" class="p-8 text-center text-gray-500">
        Nenhum bairro cadastrado
      </div>
    </div>
    
    <!-- Add/Edit Neighborhood Modal -->
    <div v-if="showAddModal || showEditModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl p-6 w-full max-w-md mx-4">
        <h2 class="text-xl font-bold mb-4">{{ editingNeighborhood ? 'Editar Bairro' : 'Novo Bairro' }}</h2>
        
        <form @submit.prevent="editingNeighborhood ? updateNeighborhood() : addNeighborhood()">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Nome do Bairro</label>
              <input v-model="neighborhoodForm.name" type="text" required class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
              <input v-model="neighborhoodForm.city" type="text" required class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Taxa de Entrega (centavos)</label>
                <input v-model.number="neighborhoodForm.delivery_fee" type="number" required class="w-full border rounded-lg px-3 py-2">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Tempo Estimado (min)</label>
                <input v-model.number="neighborhoodForm.estimated_time" type="number" required class="w-full border rounded-lg px-3 py-2">
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Pedido Mínimo (centavos)</label>
              <input v-model.number="neighborhoodForm.min_order_value" type="number" class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <div class="flex items-center">
              <label class="flex items-center cursor-pointer">
                <input v-model="neighborhoodForm.is_active" type="checkbox" class="mr-2">
                <span class="text-sm">Ativo</span>
              </label>
            </div>
          </div>
          
          <div class="flex justify-end gap-3 mt-6">
            <button type="button" @click="closeModal" class="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
              {{ editingNeighborhood ? 'Atualizar' : 'Salvar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores'
import { deliveryApi, companyApi } from '@/utils/api'
import { formatCurrency } from '@/utils/formatters'
import { notifyError, notifySuccess } from '@/utils/notifications'
import { confirmDialog } from '@/composables/useConfirm'
import type { NeighborhoodDelivery } from '@/types'

const authStore = useAuthStore()

const neighborhoods = ref<NeighborhoodDelivery[]>([])
const showAddModal = ref(false)
const showEditModal = ref(false)
const editingNeighborhood = ref<NeighborhoodDelivery | null>(null)

// Taxa única de entrega
const flatFeeEnabled = ref(false)
const flatFeeValueReais = ref<number | null>(null)
const currentFlatFee = ref<number | null>(null)  // valor salvo, em centavos
const savingFlatFee = ref(false)
const flatFeeActive = ref(false)

const neighborhoodForm = ref({
  name: '',
  city: '',
  delivery_fee: 0,
  estimated_time: 30,
  min_order_value: 0,
  is_active: true
})

const loadNeighborhoods = async () => {
  const companyId = authStore.companyId || 1
  try {
    const res = await deliveryApi.getNeighborhoodsAdmin(companyId)
    neighborhoods.value = res.data
  } catch (error) {
    console.error('Erro ao carregar bairros:', error)
  }
}

const loadFlatFee = async () => {
  const companyId = authStore.companyId || 1
  try {
    const res = await companyApi.getAdmin(companyId)
    const fee = res.data.delivery_flat_fee
    currentFlatFee.value = fee
    flatFeeActive.value = fee !== null && fee !== undefined
    flatFeeEnabled.value = flatFeeActive.value
    flatFeeValueReais.value = flatFeeActive.value ? fee / 100 : null
  } catch (error) {
    console.error('Erro ao carregar taxa única:', error)
  }
}

const saveFlatFee = async () => {
  const companyId = authStore.companyId || 1
  
  if (flatFeeEnabled.value) {
    if (flatFeeValueReais.value === null || flatFeeValueReais.value < 0) {
      notifyError('Informe um valor válido para a taxa única')
      return
    }
    
    const activeNeighborhoods = neighborhoods.value.filter(n => n.is_active)
    if (activeNeighborhoods.length > 0) {
      const confirmed = await confirmDialog({
        title: 'Taxa única de entrega',
        message: `A taxa única será aplicada a todos os bairros e ${activeNeighborhoods.length} taxa(s) por bairro cadastradas serão desativadas. Deseja continuar?`,
        confirmLabel: 'Aplicar taxa única'
      })
      if (!confirmed) return
    }
    
    savingFlatFee.value = true
    try {
      await companyApi.update(companyId, {
        delivery_flat_fee: Math.round(flatFeeValueReais.value * 100)
      })
      // Desativa as taxas por bairro
      await Promise.all(
        activeNeighborhoods.map(n => deliveryApi.updateNeighborhood(n.id, { is_active: false }))
      )
      notifySuccess('Taxa única aplicada. Taxas por bairro desativadas.')
      await loadNeighborhoods()
      await loadFlatFee()
    } catch (error: any) {
      notifyError(error.response?.data?.detail || 'Erro ao salvar taxa única')
    } finally {
      savingFlatFee.value = false
    }
  } else {
    savingFlatFee.value = true
    try {
      await companyApi.update(companyId, { delivery_flat_fee: null })
      notifySuccess('Taxa única desativada. Ative os bairros desejados para voltar a usar taxas por bairro.')
      await loadNeighborhoods()
      await loadFlatFee()
    } catch (error: any) {
      notifyError(error.response?.data?.detail || 'Erro ao salvar taxa única')
    } finally {
      savingFlatFee.value = false
    }
  }
}

const closeModal = () => {
  showAddModal.value = false
  showEditModal.value = false
  editingNeighborhood.value = null
  neighborhoodForm.value = {
    name: '',
    city: '',
    delivery_fee: 0,
    estimated_time: 30,
    min_order_value: 0,
    is_active: true
  }
}

const editNeighborhood = (neighborhood: NeighborhoodDelivery) => {
  editingNeighborhood.value = neighborhood
  neighborhoodForm.value = {
    name: neighborhood.name,
    city: neighborhood.city,
    delivery_fee: neighborhood.delivery_fee,
    estimated_time: neighborhood.estimated_time,
    min_order_value: neighborhood.min_order_value || 0,
    is_active: neighborhood.is_active
  }
  showEditModal.value = true
}

const addNeighborhood = async () => {
  const companyId = authStore.companyId || 1
  try {
    await deliveryApi.createNeighborhood({
      ...neighborhoodForm.value,
      company_id: companyId
    })
    closeModal()
    loadNeighborhoods()
    notifySuccess('Bairro criado com sucesso!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao criar bairro')
  }
}

const updateNeighborhood = async () => {
  if (!editingNeighborhood.value) return
  
  try {
    await deliveryApi.updateNeighborhood(editingNeighborhood.value.id, neighborhoodForm.value)
    closeModal()
    loadNeighborhoods()
    notifySuccess('Bairro atualizado com sucesso!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao atualizar bairro')
  }
}

const toggleActive = async (neighborhood: NeighborhoodDelivery) => {
  try {
    await deliveryApi.updateNeighborhood(neighborhood.id, { is_active: !neighborhood.is_active })
    neighborhood.is_active = !neighborhood.is_active
  } catch (error) {
    notifyError('Erro ao atualizar status')
  }
}

const deleteNeighborhood = async (neighborhood: NeighborhoodDelivery) => {
  if (!await confirmDialog({
    title: 'Confirmar exclusão',
    message: `Tem certeza que deseja excluir o bairro "${neighborhood.name}"?`,
    confirmLabel: 'Excluir',
    danger: true
  })) return
  
  try {
    await deliveryApi.deleteNeighborhood(neighborhood.id)
    loadNeighborhoods()
    notifySuccess('Bairro excluído com sucesso!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao excluir bairro')
  }
}

onMounted(() => {
  loadNeighborhoods()
  loadFlatFee()
})
</script>
